import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, LinearProgress, Pagination, Stack,
    Select, MenuItem, FormControl, Chip, Collapse, Tooltip,
    IconButton, InputBase,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import KzqTicketRegistryItem from '@LabelAnalyticsDashboardsLabelViewCharts/KzqTicketRegistryItem';
import KzqTicketRegistryViewService from '@Helpers/URLHelpers/KaiziqResolve/views/KzqTicketRegistryViewService';
import ErrorHandler from '@Root/ErrorHandler';
import './KzqTicketRegistryList.scss';

const DATE_PRESETS = [
    { label: 'All Time',    id: 'all' },
    { label: 'Today',       id: 'today' },
    { label: 'This Week',   id: 'thisWeek' },
    { label: 'Last Week',   id: 'lastWeek' },
    { label: 'Last Month',  id: 'lastMonth' },
    { label: 'Last 3 Mo.',  id: 'last3months' },
    { label: 'Custom',      id: 'custom' },
];

const getPresetRange = (id) => {
    const now = new Date();
    switch (id) {
        case 'all': return { from: null, to: null };
        case 'today': {
            const s = new Date(now); s.setHours(0, 0, 0, 0);
            return { from: s.getTime(), to: Date.now() };
        }
        case 'thisWeek': {
            const s = new Date(now); s.setDate(now.getDate() - now.getDay()); s.setHours(0, 0, 0, 0);
            return { from: s.getTime(), to: Date.now() };
        }
        case 'lastWeek': {
            const s = new Date(now); s.setDate(now.getDate() - now.getDay() - 7); s.setHours(0, 0, 0, 0);
            const e = new Date(s); e.setDate(s.getDate() + 6); e.setHours(23, 59, 59, 999);
            return { from: s.getTime(), to: e.getTime() };
        }
        case 'lastMonth': {
            const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const e = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
            return { from: s.getTime(), to: e.getTime() };
        }
        case 'last3months': {
            const s = new Date(now); s.setMonth(now.getMonth() - 3); s.setHours(0, 0, 0, 0);
            return { from: s.getTime(), to: Date.now() };
        }
        default: return { from: null, to: null };
    }
};

export default function KzqTicketRegistryList({ labelData }) {
    const [rows, setRows]                       = useState([]);
    const [loading, setLoading]                 = useState(false);
    const [exporting, setExporting]             = useState(false);
    const [page, setPage]                       = useState(1);
    const [size, setSize]                       = useState(15);
    const [totalPages, setTotalPages]           = useState(1);
    const [totalItems, setTotalItems]           = useState(0);
    const [activePreset, setActivePreset]       = useState('all');
    const [showCustom, setShowCustom]           = useState(false);
    const [customFrom, setCustomFrom]           = useState(null);
    const [customTo, setCustomTo]               = useState(null);
    const [dateRange, setDateRange]             = useState({ from: null, to: null });
    const [activeStatusFilters, setActiveStatusFilters] = useState([]);  // [{id, label, color}]
    const [searchQuery, setSearchQuery]         = useState('');
    const [, setKnownStatuses]     = useState({});  // id -> {label, color}
    const [showSearch, setShowSearch]           = useState(false);
    const searchRef                             = useRef();

    const svc = new KzqTicketRegistryViewService();
    const errorHandler = new ErrorHandler();
    const cn = 'kzqTicketRegistryList';

    useEffect(() => { fetchPage(1, size, dateRange, activeStatusFilters, searchQuery); }, [labelData?.id]); // eslint-disable-line

    const buildFilter = (pg, sz, range, statusFilters, query) => ({
        kzqTicketRegistryViewFilter: {
            registryId:    labelData?.id,
            registryType:  labelData?.registryType,
            ...(statusFilters?.length ? { statusIdList: statusFilters.map(s => s.id) } : {}),
            ...(range?.from ? { dateFrom: range.from } : {}),
            ...(range?.to   ? { dateTo:   range.to   } : {}),
            ...(query?.trim() ? { searchQuery: query.trim() } : {}),
        },
        page: pg - 1,
        size: sz,
    });

    const fetchPage = async (pg, sz, range, statusFilters, query) => {
        try {
            setLoading(true);
            const data = await svc.getListFiltered(buildFilter(pg, sz, range, statusFilters, query));
            const outer = JSON.parse(data?.body);
            const res = JSON.parse(outer?.body);
            const list = res?.result?.ticketRegistryList || [];
            setRows(list);
            setTotalItems(Number(res?.result?.totalItems ?? 0));
            setTotalPages(Number(res?.result?.totalPages ?? 1) || 1);
            setPage(pg);
            // accumulate known statuses for filter chips
            const seen = {};
            list.forEach(r => { if (r.statusId != null) seen[r.statusId] = { id: r.statusId, label: r.statusLabel, color: r.statusColor }; });
            setKnownStatuses(prev => ({ ...prev, ...seen }));
        } catch (err) {
            errorHandler.handleError(err);
        }
        setLoading(false);
    };

    const handlePresetClick = (id) => {
        setActivePreset(id);
        if (id === 'custom') { setShowCustom(true); return; }
        setShowCustom(false);
        const range = getPresetRange(id);
        setDateRange(range);
        fetchPage(1, size, range, activeStatusFilters, searchQuery);
    };

    const handleCustomApply = () => {
        if (!customFrom || !customTo) return;
        const range = { from: customFrom.startOf('day').valueOf(), to: customTo.endOf('day').valueOf() };
        setDateRange(range);
        setShowCustom(false);
        fetchPage(1, size, range, activeStatusFilters, searchQuery);
    };

    const handleStatusClick = (status) => {
        setActiveStatusFilters(prev => {
            if (prev.find(s => s.id === status.id)) return prev;
            const next = [...prev, status];
            fetchPage(1, size, dateRange, next, searchQuery);
            return next;
        });
    };

    const removeStatusFilter = (statusId) => {
        setActiveStatusFilters(prev => {
            const next = prev.filter(s => s.id !== statusId);
            fetchPage(1, size, dateRange, next, searchQuery);
            return next;
        });
    };

    const handleSearchSubmit = (e) => {
        if (e.key === 'Enter' || e.type === 'blur') {
            fetchPage(1, size, dateRange, activeStatusFilters, searchQuery);
        }
    };

    const clearSearch = () => {
        setSearchQuery('');
        fetchPage(1, size, dateRange, activeStatusFilters, '');
    };

    const handlePageChange = (_, v) => fetchPage(v, size, dateRange, activeStatusFilters, searchQuery);

    const handleSizeChange = (e) => {
        const s = e.target.value;
        setSize(s);
        fetchPage(1, s, dateRange, activeStatusFilters, searchQuery);
    };

    const handleExportExcel = async () => {
        try {
            setExporting(true);
            const filterObj = {
                kzqTicketRegistryViewFilter: {
                    registryId:   labelData?.id,
                    registryType: labelData?.registryType,
                    ...(activeStatusFilters?.length ? { statusIdList: activeStatusFilters.map(s => s.id) } : {}),
                    ...(dateRange?.from ? { dateFrom: dateRange.from } : {}),
                    ...(dateRange?.to   ? { dateTo:   dateRange.to   } : {}),
                    ...(searchQuery?.trim() ? { searchQuery: searchQuery.trim() } : {}),
                },
            };
            const data = await svc.export(filterObj);
            const outer = JSON.parse(data?.body);
            const res = JSON.parse(outer?.body);
            const raw = res?.file;
            if (!raw) return;
            const base64 = raw.startsWith('data:') ? raw.split(',')[1] : raw;
            const binary = atob(base64.replace(/\s/g, ''));
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
            const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = res?.fileName || `${(labelData?.name || 'card').replace(/[^\w-]/g, '_')}_tickets.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            errorHandler.handleError(err);
        }
        setExporting(false);
    };

    const hasActiveFilters = activePreset !== 'all' || activeStatusFilters.length > 0 || searchQuery.trim();
    const presetLabel = DATE_PRESETS.find(p => p.id === activePreset)?.label;

    return (
        <div className={cn}>
            {/* Header */}
            <Box className={`${cn}__header`}>
                <Box className={`${cn}__header__left`}>
                    <Typography className={`${cn}__header__title`}>Ticket Usage</Typography>
                    <Chip
                        size="small"
                        label={`${totalItems} ${totalItems === 1 ? 'ticket' : 'tickets'}`}
                        className={`${cn}__header__countChip`}
                    />
                </Box>
                <Box className={`${cn}__header__right`}>
                    {showSearch ? (
                        <Box className={`${cn}__searchBox`}>
                            <SearchIcon className={`${cn}__searchBox__icon`} />
                            <InputBase
                                autoFocus
                                ref={searchRef}
                                className={`${cn}__searchBox__input`}
                                placeholder="Search ticket, title, submitter..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                onKeyDown={handleSearchSubmit}
                                onBlur={handleSearchSubmit}
                            />
                            {searchQuery && (
                                <IconButton size="small" onClick={clearSearch} className={`${cn}__searchBox__clear`}>
                                    <CloseIcon sx={{ fontSize: '0.8rem' }} />
                                </IconButton>
                            )}
                        </Box>
                    ) : (
                        <Tooltip title="Search">
                            <IconButton size="small" onClick={() => setShowSearch(true)} className={`${cn}__iconBtn`}>
                                <SearchIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title={exporting ? 'Exporting...' : 'Export Excel'}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleExportExcel}
                                disabled={exporting || totalItems === 0}
                                className={`${cn}__iconBtn`}
                            >
                                <FileDownloadOutlinedIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            {/* Filter Bar */}
            <Box className={`${cn}__filterBar`}>
                <FilterListIcon className={`${cn}__filterBar__icon`} />
                <Box className={`${cn}__filterBar__presets`}>
                    {DATE_PRESETS.map(p => (
                        <button
                            key={p.id}
                            className={`${cn}__filterBar__preset${activePreset === p.id ? ' active' : ''}`}
                            onClick={() => handlePresetClick(p.id)}
                        >
                            {p.label}
                        </button>
                    ))}
                </Box>
            </Box>

            {/* Custom date range */}
            <Collapse in={showCustom && activePreset === 'custom'}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box className={`${cn}__customRange`}>
                        <DatePicker label="From" value={customFrom} onChange={setCustomFrom}
                            slotProps={{ textField: { size: 'small', className: `${cn}__customRange__picker` } }} />
                        <DatePicker label="To" value={customTo} onChange={setCustomTo} minDate={customFrom}
                            slotProps={{ textField: { size: 'small', className: `${cn}__customRange__picker` } }} />
                        <button className={`${cn}__customRange__applyBtn`} onClick={handleCustomApply} disabled={!customFrom || !customTo}>
                            Apply
                        </button>
                    </Box>
                </LocalizationProvider>
            </Collapse>

            {/* Active filter chips */}
            {hasActiveFilters && (
                <Box className={`${cn}__activeFilters`}>
                    {activePreset !== 'all' && (
                        <Chip size="small" label={presetLabel} className={`${cn}__filterChip`}
                            onDelete={() => handlePresetClick('all')} />
                    )}
                    {activeStatusFilters.map(s => (
                        <Chip
                            key={s.id}
                            size="small"
                            label={s.label}
                            className={`${cn}__filterChip ${cn}__filterChip--status`}
                            style={{ '--status-color': s.color }}
                            onDelete={() => removeStatusFilter(s.id)}
                        />
                    ))}
                    {searchQuery.trim() && (
                        <Chip size="small" label={`"${searchQuery.trim()}"`} className={`${cn}__filterChip`}
                            onDelete={clearSearch} />
                    )}
                </Box>
            )}

            {/* Content */}
            <Box className={`${cn}__content`}>
                {loading ? (
                    <Box className={`${cn}__loader`}>
                        <LinearProgress sx={{ borderRadius: 1 }} />
                        <Typography variant="caption" className={`${cn}__loader__text`}>Loading tickets...</Typography>
                    </Box>
                ) : rows.length === 0 ? (
                    <Box className={`${cn}__empty`}>
                        <Typography className={`${cn}__empty__title`}>No tickets found</Typography>
                        <Typography className={`${cn}__empty__sub`}>
                            {hasActiveFilters ? 'Try adjusting your filters.' : 'No tickets have been linked to this card yet.'}
                        </Typography>
                    </Box>
                ) : (
                    <Box className={`${cn}__list`}>
                        {rows.map((item, i) => (
                            <KzqTicketRegistryItem
                                key={item.rn ?? i}
                                item={item}
                                onStatusClick={handleStatusClick}
                            />
                        ))}
                    </Box>
                )}
            </Box>

            {/* Pagination */}
            {!loading && rows.length > 0 && (
                <Box className={`${cn}__pagination`}>
                    <Stack>
                        <Pagination
                            count={Math.max(totalPages, 1)}
                            page={page}
                            onChange={handlePageChange}
                            size="small"
                            showFirstButton
                            showLastButton
                            siblingCount={1}
                            className={`${cn}__pagination__nav`}
                        />
                    </Stack>
                    <Box className={`${cn}__pagination__size`}>
                        <Typography variant="caption" className={`${cn}__pagination__size__label`}>Per page:</Typography>
                        <FormControl size="small">
                            <Select value={size} onChange={handleSizeChange} className={`${cn}__pagination__size__select`}>
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={15}>15</MenuItem>
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
            )}
        </div>
    );
}
